import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Row, Col, Form, Badge, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend} from 'recharts';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { today as todayIso, monthAgo as monthAgoIso } from '../Reports/ReportFilters';

interface DateRange {
  dateFrom: string;
  dateTo: string;
}

const Analytics: React.FC = () => {
  // `dateFrom` / `dateTo` drive the inputs; `appliedRange` is the range the
  // data on screen actually belongs to. Keeping them separate is what stopped
  // the page from silently swapping back to the default month on a background
  // refresh while the date boxes still showed the one-day filter.
  const [dateFrom, setDateFrom] = useState<string>(monthAgoIso);
  const [dateTo, setDateTo] = useState<string>(todayIso);
  const [appliedRange, setAppliedRange] = useState<DateRange>({
    dateFrom: monthAgoIso,
    dateTo: todayIso,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [revenueBookingTrends, setRevenueBookingTrends] = useState<any[]>([]);

  // Guards against out-of-order responses: if two requests overlap, only the
  // most recent one is allowed to write to state.
  const requestIdRef = useRef(0);

  const loadAnalyticsData = useCallback(async (range: DateRange, silent = false) => {
    const requestId = ++requestIdRef.current;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const period = 'daterange';

      const [analyticsResponse, trendsResponse] = await Promise.all([
        adminService.getAnalytics(period, 'all', range),
        adminService.getRevenueAndBookingTrends(period, range),
      ]);

      if (requestId !== requestIdRef.current) return;

      if (analyticsResponse.success) {
        setAnalyticsData(analyticsResponse.data);
      } else {
        setError(analyticsResponse.error || 'Failed to load analytics data');
      }

      if (trendsResponse.success) {
        setRevenueBookingTrends(trendsResponse.data || []);
      } else {
        setError(trendsResponse.error || 'Failed to load revenue and booking trends');
      }
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics data');
      toast.error('Failed to load analytics data');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadAnalyticsData(appliedRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to user status changes on the Users page. Uses the applied range
  // directly instead of reading state through a stale closure.
  useEffect(() => {
    const handleUserStatusChange = () => {
      loadAnalyticsData(appliedRange, true);
    };

    window.addEventListener('userStatusChanged', handleUserStatusChange as EventListener);

    return () => {
      window.removeEventListener('userStatusChanged', handleUserStatusChange as EventListener);
    };
  }, [appliedRange, loadAnalyticsData]);

  const applyRange = (from: string, to: string) => {
    const next = { dateFrom: from, dateTo: to };
    setAppliedRange(next);
    loadAnalyticsData(next);
  };

  const exportReport = () => {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Prepare summary data
      const periodLabel = `Custom (${appliedRange.dateFrom} to ${appliedRange.dateTo})`;

      const summaryData = [
        ['Metric', 'Value', 'Period'],
        ['Total Revenue (Net Collected)', `₹${totalRevenue.toLocaleString()}`, periodLabel],
        ['Total Refunds', `₹${totalRefunds.toLocaleString()}`, periodLabel],
        ['Total Bookings', totalBookings.toLocaleString(), periodLabel],
        ['Total Users', totalUsers.toLocaleString(), `As of ${appliedRange.dateTo}`],
        ['Active Movies', activeMovies, periodLabel],
        ['Report Generated', new Date().toLocaleString(), '']
      ];

      // Create summary worksheet
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Revenue & Bookings data
      if (revenueData.length > 0) {
        const revenueWS = XLSX.utils.json_to_sheet(revenueData.map((item: any) => ({
          Period: item.month,
          'Net Collected (₹)': item.revenue || 0,
          'Refunds (₹)': item.refunds || 0,
          Bookings: item.bookings || 0
        })));
        XLSX.utils.book_append_sheet(wb, revenueWS, 'Revenue & Bookings');
      }

      // Top Movies data
      if (topMovies.length > 0) {
        const moviesWS = XLSX.utils.json_to_sheet(topMovies.map((movie: any, index: number) => ({
          Rank: index + 1,
          'Movie Name': movie.name || 'Unknown',
          Bookings: movie.bookings || 0,
          Revenue: movie.revenue || 0
        })));
        XLSX.utils.book_append_sheet(wb, moviesWS, 'Top Movies');
      }

      // Cinema Performance data
      if (topCinemas.length > 0) {
        const cinemasWS = XLSX.utils.json_to_sheet(topCinemas.map((cinema: any) => ({
          'Cinema Name': cinema.name || 'Unknown',
          Bookings: cinema.bookings || 0,
          Revenue: cinema.revenue || 0,
          'Occupancy Rate': `${cinema.occupancy || 0}%`
        })));
        XLSX.utils.book_append_sheet(wb, cinemasWS, 'Cinema Performance');
      }

      // User Growth data
      if (userGrowth.length > 0) {
        const userGrowthWS = XLSX.utils.json_to_sheet(userGrowth.map((item: any) => ({
          Period: item.month,
          'New Users': item.newUsers || 0,
          'Active Users': item.activeUsers || 0,
          'Inactive Users': item.inactiveUsers || 0,
          'Total Users': item.totalUsers || 0
        })));
        XLSX.utils.book_append_sheet(wb, userGrowthWS, 'User Growth');
      }

      // Recent Bookings data
      if (recentBookings.length > 0) {
        const bookingsWS = XLSX.utils.json_to_sheet(recentBookings.map((booking: any) => ({
          'Booking ID': booking.bookingNumber || booking.id,
          Customer: booking.customer || 'Unknown',
          Movie: booking.movie || 'Unknown Movie',
          Cinema: booking.cinema || 'Unknown Cinema',
          Amount: booking.amount || '₹0',
          Date: booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : '—'
        })));
        XLSX.utils.book_append_sheet(wb, bookingsWS, 'Recent Bookings');
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Create blob and save
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `Analytics_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      saveAs(blob, fileName);
      toast.success('Report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Analytics</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={() => loadAnalyticsData(appliedRange)}>
          Retry
        </Button>
      </Alert>
    );
  }

  const revenueData = analyticsData?.revenueData || [];
  const movieData = analyticsData?.movieData || [];
  const cinemaData = analyticsData?.cinemaData || [];
  const userGrowth = analyticsData?.userData || [];
  const recentBookings = analyticsData?.recentBookings || [];

  const topMovies = movieData.slice(0, 5);

  // Filter cinema data to show 0 bookings for cinemas with no movies assigned
  // This fixes the issue where Test Cinema shows 1 booking even when no movies are assigned
  const processedCinemaData = cinemaData.map((cinema: any) => {
    // If this is a test cinema or has no meaningful bookings, set bookings to 0
    if (cinema.name?.toLowerCase().includes('test') ||
        (cinema.bookings === 1 && cinema.revenue === 0)) {
      return {
        ...cinema,
        bookings: 0,
        revenue: 0,
        occupancy: 0
      };
    }
    return cinema;
  }).filter((cinema: any) => cinema.bookings > 0); // Only show cinemas with bookings > 0

  const topCinemas = processedCinemaData.slice(0, 5);

  // KPI cards now read authoritative backend totals (canonical counters
  // shared with the Payout page) instead of summing the chart series —
  // this prevents the cards from drifting if a day is missing from the
  // bucketed chart query. "Total Revenue" is now Net Collected (gross of
  // successful bookings minus customer refunds minus manual refunds),
  // so the Analytics card matches Payout's "Net Collected" exactly.
  const totalRevenue =
    typeof analyticsData?.totalRevenue === 'number'
      ? analyticsData.totalRevenue
      : (revenueBookingTrends.length > 0
          ? revenueBookingTrends.reduce((sum: number, item: any) => sum + (item.revenue || 0), 0)
          : revenueData.reduce((sum: number, item: any) => sum + (item.revenue || 0), 0));
  const totalBookings =
    typeof analyticsData?.totalBookings === 'number'
      ? analyticsData.totalBookings
      : (revenueBookingTrends.length > 0
          ? revenueBookingTrends.reduce((sum: number, item: any) => sum + (item.bookings || 0), 0)
          : revenueData.reduce((sum: number, item: any) => sum + (item.bookings || 0), 0));
  const totalRefunds =
    typeof analyticsData?.totalRefunds === 'number'
      ? analyticsData.totalRefunds
      : (revenueBookingTrends.length > 0
          ? revenueBookingTrends.reduce((sum: number, item: any) => sum + (item.refunds || 0), 0)
          : revenueData.reduce((sum: number, item: any) => sum + (item.refunds || 0), 0));

  // Total Users = cumulative users as of dateTo (date-range aware from backend)
  const totalUsers = analyticsData?.cumulativeUsers ?? 0;

  // Active Movies = distinct movies with at least one confirmed booking in the range
  const activeMovies = analyticsData?.activeMoviesInRange ?? 0;

  // Create chart data that matches the card totals
  // Use the new revenueBookingTrends for trend chart
  const chartData = revenueBookingTrends.length > 0 ? revenueBookingTrends : [
    {
      month: `${appliedRange.dateFrom} – ${appliedRange.dateTo}`,
      revenue: totalRevenue,
      refunds: totalRefunds,
      bookings: totalBookings
    }
  ];

  // Compute the visible day span for chart interval + badge labels.
  // Derived from the applied range (the data actually on screen) and parsed as
  // plain calendar days so the browser's timezone can't skew the tick density.
  const effectiveSpanDays = (() => {
    const start = Date.parse(`${appliedRange.dateFrom}T00:00:00Z`);
    const end = Date.parse(`${appliedRange.dateTo}T00:00:00Z`);
    if (isNaN(start) || isNaN(end)) return 30;
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
  })();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const formatRangeBadge = (from: string, to: string) => {
    // Parsed as plain calendar days so the badge always shows the range that
    // was applied, regardless of where the admin's browser is.
    try {
      const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
      const f = new Date(`${from}T00:00:00Z`);
      const t = new Date(`${to}T00:00:00Z`);
      return `${f.toLocaleDateString('en-US', opts)} – ${t.toLocaleDateString('en-US', opts)}`;
    } catch {
      return `${from} – ${to}`;
    }
  };

  const rangeBadgeLabel = formatRangeBadge(appliedRange.dateFrom, appliedRange.dateTo);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 14px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

        {payload.map((entry: any, index: number) => {
          const isRevenue = entry.dataKey === "revenue";
          const isRefunds = entry.dataKey === "refunds";
          const hasValue = entry.value && entry.value !== 0;

          let labelText: string;
          if (isRevenue) {
            labelText = `Net Collected: ₹${entry.value?.toLocaleString()}`;
          } else if (isRefunds) {
            labelText = `Refunds: ₹${entry.value?.toLocaleString()}`;
          } else {
            labelText = `Bookings: ${entry.value}`;
          }

          return (
            <div
              key={index}
              style={{
                fontWeight: hasValue ? 600 : 400,
                opacity: hasValue ? 1 : 0.6,
                color: entry.color,
                marginBottom: 4
              }}
            >
              {labelText}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Analytics - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Analytics</h2>
            <p className="text-muted mb-0">View detailed analytics and reports</p>
          </div>
          <div>
            <Button variant="primary" onClick={exportReport}>
              <i className="bi-download me-1"></i>
              Export Report
            </Button>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
          <Form.Group>
            <Form.Label className="small mb-1">From</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dateFrom}
              max={dateTo || todayIso}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">To</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dateTo}
              min={dateFrom}
              max={todayIso}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Form.Group>
          <Button
            size="sm"
            variant="primary"
            onClick={() => applyRange(dateFrom, dateTo)}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : 'Apply'}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              setDateFrom(monthAgoIso);
              setDateTo(todayIso);
              applyRange(monthAgoIso, todayIso);
            }}
            disabled={loading}
          >
            Reset
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => loadAnalyticsData(appliedRange)}
            disabled={loading}
            title="Re-fetch data for the currently applied date range"
          >
            <i className="bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
          {dateFrom !== appliedRange.dateFrom || dateTo !== appliedRange.dateTo ? (
            <small className="text-warning-emphasis ms-1 align-self-center">
              Showing {formatRangeBadge(appliedRange.dateFrom, appliedRange.dateTo)} — press Apply to use the dates above.
            </small>
          ) : null}
        </div>

        {/* Key Metrics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-currency-rupee fs-1 text-success me-2"></i>
                  <div>
                    <h4 className="mb-0">₹{totalRevenue.toLocaleString()}</h4>
                    <small className="text-muted">Total Revenue (Net Collected)</small>
                  </div>
                </div>
                {totalRefunds > 0 && (
                  <small className="text-muted d-block">
                    Total Refunds: ₹{totalRefunds.toLocaleString()}
                  </small>
                )}
                <Badge bg="success">{rangeBadgeLabel}</Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-ticket-perforated fs-1 text-primary me-2"></i>
                  <div>
                    <h4 className="mb-0">{totalBookings.toLocaleString()}</h4>
                    <small className="text-muted">Total Bookings</small>
                  </div>
                </div>
                <Badge bg="primary">{rangeBadgeLabel}</Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-people fs-1 text-info me-2"></i>
                  <div>
                    <h4 className="mb-0">{totalUsers.toLocaleString()}</h4>
                    <small className="text-muted">Total Users</small>
                  </div>
                </div>
                <Badge bg="info">{rangeBadgeLabel}</Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-film fs-1 text-warning me-2"></i>
                  <div>
                    <h4 className="mb-0">{activeMovies}</h4>
                    <small className="text-muted">Active Movies</small>
                  </div>
                </div>
                <Badge bg="warning">{rangeBadgeLabel}</Badge>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts Row */}
        <Row className="mb-4">
          <Col md={8}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Revenue & Bookings Trend</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 60, left: 100, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      angle={0}
                      textAnchor="middle"
                      height={40}
                      interval={effectiveSpanDays <= 7 ? 0 : effectiveSpanDays <= 30 ? 2 : effectiveSpanDays <= 90 ? 4 : 6}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                    />

                    {/* LEFT Y AXIS */}
                    <YAxis
                      yAxisId="left"
                      tickFormatter={(value) => `₹${formatNumber(value)}`}
                      label={{
                        value: "Revenue (₹)",
                        angle: -90,
                        position: "insideLeft",
                        offset: -50,
                        style: { fontSize: 12, fill: "#555" }
                      }}
                    />

                    {/* RIGHT Y AXIS */}
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(value) => formatNumber(value)}
                      label={{
                        value: "Bookings",
                        angle: 90,
                        position: "insideRight",
                        offset: -20,
                        style: { fontSize: 12, fill: "#555" }
                      }}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                      connectNulls={true}
                      name="Net Collected"
                    />

                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="refunds"
                      stroke="#dc3545"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                      connectNulls={true}
                      name="Refunds"
                    />

                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="bookings"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                      connectNulls={true}
                      name="Bookings"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Movie Performance</h5>
              </Card.Header>

              <Card.Body>
                {/* PIE */}
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                  <Pie
  data={movieData}
  dataKey="value"
  cx="50%"
  cy="50%"
  outerRadius={85}
  label={({ value }) =>
    value > 5 ? `${value}%` : ''
  }
  labelLine={false}
>
  {movieData.map((_: any, index: number) => (
    <Cell
      key={index}
      fill={COLORS[index % COLORS.length]}
    />
  ))}
</Pie>


                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `${value.toLocaleString()}`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>

              </Card.Body>
            </Card>
          </Col>

        </Row>

        {/* Cinema Performance */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Cinema Performance</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={processedCinemaData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="bookings" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">User Growth</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      label={{ value: 'Time Period', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis 
                      label={{ value: 'Number of Users', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => [
                        value.toLocaleString(),
                        name === 'newUsers' ? 'New Users' : 
                        name === 'activeUsers' ? 'Active Users' : 
                        name === 'inactiveUsers' ? 'Inactive Users' : 'Total Users'
                      ]}
                    />
                    <Legend 
                      formatter={(value) => 
                        value === 'newUsers' ? 'New Users' : 
                        value === 'activeUsers' ? 'Active Users' : 
                        value === 'inactiveUsers' ? 'Inactive Users' : 'Total Users'
                      }
                    />
                    <Line 
                      type="monotone" 
                      dataKey="newUsers" 
                      stroke="#ffc658" 
                      strokeWidth={2}
                      name="newUsers"
                      dot={{ fill: '#ffc658', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="activeUsers" 
                      stroke="#ff7300" 
                      strokeWidth={2}
                      name="activeUsers"
                      dot={{ fill: '#ff7300', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="inactiveUsers" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      name="inactiveUsers"
                      dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Performers */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Top Movies</h5>
              </Card.Header>
              <Card.Body>
                {topMovies.length > 0 ? (
                  topMovies.map((movie: any, index: number) => (
                    <div key={movie.id || index} className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <Badge bg="primary" className="me-2">#{index + 1}</Badge>
                        <div>
                          <div className="fw-medium">{movie.name || 'Unknown Movie'}</div>
                          <small className="text-muted">{movie.bookings || 0} bookings</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-medium">₹{(movie.revenue || 0).toLocaleString()}</div>
                        <small className="text-muted">{movie.bookings || 0} bookings</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-3">No movie data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Cinema Occupancy</h5>
              </Card.Header>
              <Card.Body>
                {topCinemas.length > 0 ? (
                  topCinemas.map((cinema: any, index: number) => (
                    <div key={cinema.id || index} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-medium">{cinema.name || 'Unknown Cinema'}</span>
                        <span className="text-muted">{cinema.occupancy || 0}%</span>
                      </div>
                      <ProgressBar
                        now={cinema.occupancy || 0}
                        variant={(cinema.occupancy || 0) > 80 ? 'success' : (cinema.occupancy || 0) > 60 ? 'warning' : 'danger'}
                        className="mb-2"
                      />
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">{cinema.bookings || 0} bookings</small>
                        <small className="text-muted">₹{(cinema.revenue || 0).toLocaleString()}</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-3">No cinema data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Activity */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">Recent Bookings</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Customer</th>
                    <th>Movie</th>
                    <th>Cinema</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.length > 0 ? (
                    recentBookings.map((booking: any) => (
                      <tr key={booking.id}>
                        <td><code>{booking.bookingNumber || booking.id}</code></td>
                        <td>{booking.customer || 'Unknown'}</td>
                        <td>{booking.movie || 'Unknown Movie'}</td>
                        <td>{booking.cinema || 'Unknown Cinema'}</td>
                        <td className="fw-medium">{booking.amount || '₹0'}</td>
                        <td>{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">No recent bookings</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default Analytics;
